import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import TaskItem from './TaskItem';
import {
  Plus,
  ChevronDown,
  ChevronRight,
  SlidersHorizontal,
  Calendar,
  Tag,
  Check,
  Menu,
} from 'lucide-react';
import { DarkModeToggle } from './DarkModeToggle';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { format } from 'date-fns';

const TaskList = ({ onMenuClick }) => {
  const {
    lists,
    tags,
    selectedListId,
    tasks,
    addTask,
    sortBy,
    setSortBy,
    showCompleted,
    setShowCompleted,
  } = useApp();

  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState(null);
  const [newTaskListId, setNewTaskListId] = useState('');
  const [newTaskTagIds, setNewTaskTagIds] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  const currentList = lists.find(l => l.id === selectedListId);
  const defaultList = lists.find(l => l.isDefault) || lists[0];

  const getTitle = () => {
    if (selectedListId === 'all') return 'All Tasks';
    if (selectedListId === 'today') return 'Today';
    if (selectedListId === 'upcoming') return 'Upcoming';
    return currentList?.name || 'Tasks';
  };

  // Filter and group tasks
  const filteredTasks = useMemo(() => {
    let filtered = tasks;

    if (selectedListId === 'today') {
      const today = new Date().toDateString();
      filtered = tasks.filter(t => {
        if (!t.dueDate) return false;
        return new Date(t.dueDate).toDateString() === today;
      });
    } else if (selectedListId === 'upcoming') {
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);
      filtered = tasks.filter(t => {
        if (!t.dueDate) return false;
        const dueDate = new Date(t.dueDate);
        return dueDate > today && dueDate <= nextWeek;
      });
    } else if (selectedListId !== 'all') {
      filtered = tasks.filter(t => t.listId === selectedListId);
    }

    if (!showCompleted) {
      filtered = filtered.filter(t => !t.completed);
    }

    return filtered;
  }, [tasks, selectedListId, showCompleted]);

  // Group tasks by time
  const groupedTasks = useMemo(() => {
    if (sortBy !== 'time') {
      return { all: filteredTasks };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    const groups = {
      overdue: [],
      today: [],
      tomorrow: [],
      upcoming: [],
      someday: [],
    };

    filteredTasks.forEach(task => {
      if (!task.dueDate) {
        groups.someday.push(task);
      } else {
        const dueDate = new Date(task.dueDate);
        dueDate.setHours(0, 0, 0, 0);

        if (dueDate < today) {
          groups.overdue.push(task);
        } else if (dueDate.getTime() === today.getTime()) {
          groups.today.push(task);
        } else if (dueDate.getTime() === tomorrow.getTime()) {
          groups.tomorrow.push(task);
        } else if (dueDate < nextWeek) {
          groups.upcoming.push(task);
        } else {
          groups.someday.push(task);
        }
      }
    });

    return groups;
  }, [filteredTasks, sortBy]);

  const handleAddTask = () => {
    if (newTaskTitle.trim()) {
      addTask({
        title: newTaskTitle.trim(),
        dueDate: newTaskDueDate?.toISOString() || null,
        listId: newTaskListId || (selectedListId !== 'all' && selectedListId !== 'today' && selectedListId !== 'upcoming' ? selectedListId : defaultList?.id),
        tagIds: newTaskTagIds,
      });
      setNewTaskTitle('');
      setNewTaskDueDate(null);
      setNewTaskListId('');
      setNewTaskTagIds([]);
    }
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleTag = (tagId) => {
    setNewTaskTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const renderTaskGroup = (groupTasks, title, section, color = 'text-gray-700 dark:text-gray-300') => {
    if (groupTasks.length === 0) return null;

    return (
      <div key={section} className="mb-4">
        <button
          onClick={() => toggleSection(section)}
          className="w-full flex items-center gap-2 py-2 px-1 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          {collapsedSections[section] ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
          <span className={color}>{title}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">({groupTasks.length})</span>
        </button>
        {!collapsedSections[section] && (
          <div className="space-y-2 ml-2 sm:ml-6">
            {groupTasks.map(task => (
              <TaskItem key={task.id} task={task} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <main className="flex-1 h-screen overflow-hidden flex flex-col bg-gray-50 dark:bg-gray-900 min-w-0">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors -ml-2"
            >
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{getTitle()}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="w-4 h-4" />
                  <span className="hidden sm:inline">View</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setSortBy('time')}>
                  <span className="flex items-center justify-between w-full">
                    Sort by Time
                    {sortBy === 'time' && <Check className="w-4 h-4" />}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('list')}>
                  <span className="flex items-center justify-between w-full">
                    Sort by List
                    {sortBy === 'list' && <Check className="w-4 h-4" />}
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowCompleted(!showCompleted)}>
                  <span className="flex items-center justify-between w-full">
                    Show Completed
                    {showCompleted && <Check className="w-4 h-4" />}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DarkModeToggle />
          </div>
        </div>
      </header>

      {/* Add Task Bar - Moved to top */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1 relative">
            <Input
              placeholder="Add a task..."
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
              className="pr-4 py-3 text-base"
            />
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-1 justify-between sm:justify-start">
            <div className="flex items-center gap-1">
              {/* Date picker */}
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`${newTaskDueDate ? 'text-blue-600' : 'text-gray-500'}`}
                  >
                    <Calendar className="w-5 h-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    mode="single"
                    selected={newTaskDueDate}
                    onSelect={(date) => {
                      setNewTaskDueDate(date);
                      setShowDatePicker(false);
                    }}
                    initialFocus
                  />
                  {newTaskDueDate && (
                    <div className="p-2 border-t text-center">
                      <span className="text-sm text-gray-600">
                        {format(newTaskDueDate, 'PPP')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 text-red-500"
                        onClick={() => setNewTaskDueDate(null)}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>

              {/* Tag picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`${newTaskTagIds.length > 0 ? 'text-blue-600' : 'text-gray-500'}`}
                  >
                    <Tag className="w-5 h-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="end">
                  <p className="text-xs font-semibold text-gray-500 mb-2 px-2">Tags</p>
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-gray-100 transition-colors
                        ${newTaskTagIds.includes(tag.id) ? 'bg-gray-100' : ''}`}
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span>{tag.name}</span>
                      {newTaskTagIds.includes(tag.id) && (
                        <Check className="w-4 h-4 ml-auto text-blue-600" />
                      )}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>

              {/* List picker - hidden on very small screens */}
              <div className="hidden sm:block">
                <Select value={newTaskListId} onValueChange={setNewTaskListId}>
                  <SelectTrigger className="w-28 border-none shadow-none text-gray-500">
                    <SelectValue placeholder="List" />
                  </SelectTrigger>
                  <SelectContent>
                    {lists.map(list => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add button */}
            <Button
              onClick={handleAddTask}
              disabled={!newTaskTitle.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Task List */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        {sortBy === 'time' ? (
          <>
            {renderTaskGroup(groupedTasks.overdue || [], 'Overdue', 'overdue', 'text-red-600')}
            {renderTaskGroup(groupedTasks.today || [], 'Today', 'today', 'text-blue-600')}
            {renderTaskGroup(groupedTasks.tomorrow || [], 'Tomorrow', 'tomorrow')}
            {renderTaskGroup(groupedTasks.upcoming || [], 'Upcoming', 'upcoming')}
            {renderTaskGroup(groupedTasks.someday || [], 'Someday', 'someday', 'text-gray-500')}
          </>
        ) : (
          <div className="space-y-2">
            {(groupedTasks.all || filteredTasks).map(task => (
              <TaskItem key={task.id} task={task} />
            ))}
          </div>
        )}

        {filteredTasks.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-lg font-medium">No tasks yet</p>
            <p className="text-sm">Add a task to get started</p>
          </div>
        )}
      </div>
    </main>
  );
};

export default TaskList;
